import { colors } from '@ctw/config/zui';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import type { AuthenticatedUserState } from '@ctw/shared/utils/zus-authenticated-user';
import { Document, Font, G, Page, Path, Svg, Text, View } from '@react-pdf/renderer';
import { memo, useMemo } from 'react';
import { globalStyles } from './pdf-global-styles';
import { headingStyles } from './pdf-heading-renderer';
import { PdfSectionRenderer } from './pdf-section-renderer';
import { PdfTableOfContentsRenderer } from './pdf-table-of-contents-renderer';

Font.registerHyphenationCallback((word) => (word.length < 15 ? [word] : word.split('')));

interface PdfCdaRendererProps {
  cdaDocument: CdaDocument;
  cdaDocumentSectionIdAllowlist?: Array<string>;
  patient: PatientModel;
  authenticatedUserState: AuthenticatedUserState;
}

export const PdfCdaRenderer = memo(
  ({
    patient,
    cdaDocument,
    cdaDocumentSectionIdAllowlist,
    authenticatedUserState,
  }: PdfCdaRendererProps) => {
    const allowlistedSections = useMemo(
      () =>
        cdaDocumentSectionIdAllowlist === undefined
          ? (cdaDocument?.content?.sections ?? [])
          : (cdaDocument?.content?.sections ?? []).filter((section) =>
              cdaDocumentSectionIdAllowlist.includes(section.id),
            ),
      [cdaDocumentSectionIdAllowlist, cdaDocument?.content?.sections],
    );

    const date = cdaDocument.detailFields.find((field) => field.label === 'Date')?.value;
    const author = cdaDocument.detailFields.find((field) => field.label === 'Author')?.value;
    const patientName = patient.fullName;

    return (
      <Document
        key={cdaDocument.id}
        style={globalStyles.container}
        title={
          cdaDocumentSectionIdAllowlist === undefined
            ? cdaDocument?.metadata?.document.title
            : `Partial export - ${cdaDocument?.metadata?.document.title}`
        }
      >
        <Page key={cdaDocument.id} size="A4" style={globalStyles.page}>
          <View style={headingStyles.headingContentContainer}>
            <Text style={headingStyles.h1}>{cdaDocument?.metadata?.document.title}</Text>
            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={{ color: '#21668e', fontSize: 14 }}>Sourced from</Text>
              <PdfZusLogo />
            </View>
          </View>
          {cdaDocumentSectionIdAllowlist !== undefined && (
            <View style={globalStyles.infoBox}>
              <PdfFontAwesomeInfoIcon />
              <Text style={globalStyles.infoText}>
                This is a partial PDF generated by{' '}
                {authenticatedUserState.fullName ?? authenticatedUserState.authTokenState.email} on{' '}
                {formatDateToLocalString(new Date())}
              </Text>
            </View>
          )}
          {(cdaDocument.parseError ||
            cdaDocument.content?.sections.some((section) => section.parseError !== undefined)) && (
            <View style={globalStyles.alertBox}>
              <Text style={globalStyles.alertText}>
                This document contains sections which could not be shown due to invalid XML.
                Download the XML to view in full.
              </Text>
            </View>
          )}
          {allowlistedSections.length === 0 ? (
            <>
              {cdaDocument.parseError?.message !== 'No content' ? (
                <View style={globalStyles.alertBox}>
                  <Text style={globalStyles.alertText}>
                    This document cannot be shown due to invalid XML. Download the XML to view the
                    document.
                  </Text>
                </View>
              ) : (
                <View style={globalStyles.alertBox}>
                  <Text style={globalStyles.alertText}>
                    There is no content available to display for this document.
                  </Text>
                </View>
              )}
            </>
          ) : (
            <>
              <PdfTableOfContentsRenderer
                patient={patient}
                cdaDocument={cdaDocument}
                cdaDocumentSectionIdAllowlist={cdaDocumentSectionIdAllowlist}
              />
              {allowlistedSections.map((section, index) => (
                <PdfSectionRenderer key={`section-${section.id}-${index}`} cdaSection={section} />
              ))}
              <View style={globalStyles.footer} fixed={true} wrap={false}>
                <View style={globalStyles.footerMetadata} fixed={true} wrap={false}>
                  {cdaDocument.metadata?.document.title && (
                    <Text style={globalStyles.footerTitle}>
                      {cdaDocumentSectionIdAllowlist !== undefined && 'Partial Document - '}
                      {cdaDocument.metadata.document.title}
                    </Text>
                  )}
                  <Text style={globalStyles.footerFields}>
                    {[date, author, patientName].filter(Boolean).join(' | ')}
                  </Text>
                </View>
                <View style={globalStyles.footerContext} fixed={true} wrap={false}>
                  <Text
                    style={globalStyles.footerPageNumber}
                    render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
                  />
                  <Text style={globalStyles.footerAttribution}>zus health</Text>
                </View>
              </View>
            </>
          )}
        </Page>
      </Document>
    );
  },
);

const PdfZusLogo = () => (
  <Svg style={{ width: 44, height: 23, fill: 'none' }} viewBox="0 0 128 67">
    <G>
      <Path
        d="M48.316 48.108L32.527 39l11.387-19.708c.075-.128.208-.36 0-.718-.209-.359-.475-.359-.62-.359h-22.78V0h22.774c6.852 0 12.986 3.541 16.414 9.466 3.43 5.931 3.43 13.008 0 18.933L48.316 48.108z"
        fill="#008052"
      />
      <Path
        d="M41.55 67H18.987C12.134 67 6 63.459 2.572 57.533c-3.43-5.93-3.43-13.007 0-18.932l11.386-19.709L29.747 28 18.36 47.708c-.075.128-.208.359 0 .718.209.359.475.359.626.359H41.55V67z"
        fill="#90BCD1"
      />
      <Path
        d="M41.55 67H18.987C12.134 67 6 63.46 2.572 57.534c-3.43-5.931-3.43-13.008 0-18.933l11.386-19.708L29.747 28 18.36 47.71c-.075.127-.208.358 0 .717.209.359.475.359.626.359H41.55V67z"
        fill="#73B2CF"
      />
      <Path
        d="M84.393 28.3c.307 0 .423.192.423.464v.85c0 .388.04.62-.347 1.157L74.28 44.803h10.882v2.2H71.617c-.307 0-.422-.157-.422-.423v-.886c0-.387-.04-.619.347-1.157l10.113-14.032H71.35v-2.198h13.043V28.3zm20.104 0h2.664v16.232c0 .734-.232 1.197-.967 1.504l-1.929.77c-.886.347-1.274.422-2.236.422h-5.901c-1.466 0-2.12-.272-2.972-1.117l-1.002-1c-1.042-1.042-1.199-1.43-1.199-3.16V28.3h2.665v13.725c0 .62.115.85.579 1.313l.886.886c.504.463.654.538 1.43.538h5.364c.231 0 .463-.04.654-.116l1.738-.694c.156-.075.231-.156.231-.347V28.3h-.005zm12.429 10.295c-1.199-.116-2.12-.423-2.739-1.001-.655-.62-1.002-1.58-1.002-3.084v-1.233c0-1.54.156-2.043 1.198-3.084l1.002-1.001c.887-.85 1.541-1.117 3.012-1.117h3.939c1.349 0 1.928.116 2.855.695l2.16 1.313-1.042 1.886-2.624-1.197c-.538-.232-.927-.307-1.622-.307h-3.359c-.77 0-.927.075-1.43.538l-.927.926c-.463.463-.579.694-.579 1.313v1.43c0 1 .272 1.348 1.349 1.464l7.06.653c1.466.157 2.317.463 2.931 1.082.62.62.886 1.54.886 2.853v1.233c0 1.736-.156 2.117-1.199 3.159l-1.002 1.001c-.851.85-1.505 1.117-2.971 1.117h-4.94c-1.35 0-1.929-.116-2.896-.695l-2.161-1.313 1.043-1.886 2.624 1.232c.538.231.926.272 1.621.272h4.362c.77 0 .926-.075 1.43-.538l.927-.926c.463-.463.579-.694.579-1.313v-1.349c0-1-.272-1.348-1.349-1.464l-7.142-.654.006-.005z"
        fill="#008052"
      />
    </G>
  </Svg>
);

export const PdfFontAwesomeInfoIcon = () => (
  <Svg style={{ width: 13, height: 13 }} viewBox="0 0 640 640">
    <Path
      fill={colors.info.main}
      d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM288 224C288 206.3 302.3 192 320 192C337.7 192 352 206.3 352 224C352 241.7 337.7 256 320 256C302.3 256 288 241.7 288 224zM280 288L328 288C341.3 288 352 298.7 352 312L352 400L360 400C373.3 400 384 410.7 384 424C384 437.3 373.3 448 360 448L280 448C266.7 448 256 437.3 256 424C256 410.7 266.7 400 280 400L304 400L304 336L280 336C266.7 336 256 325.3 256 312C256 298.7 266.7 288 280 288z"
    />
  </Svg>
);
